import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader, X, UtensilsCrossed } from 'lucide-react';

const HeartIcon = ({ filled, size = 16 }) => (
  <svg 
    className={filled ? 'fill-red-500 text-red-500' : ''} 
    width={size} 
    height={size} 
    fill={filled ? 'currentColor' : 'none'} 
    stroke="currentColor" 
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const CommentIcon = ({ size = 16 }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const BookmarkIcon = ({ filled, size = 16 }) => (
  <svg width={size} height={size} fill={filled ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
  </svg>
);

const formatTimeAgo = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString();
};

/**
 * BuzzCard - Compact list-card layout for feed posts
 * Thumbnail left, meta right, inline expandable comments
 */
const BuzzCard = ({
  post,
  variant = 'feed',
  onHiveClick,
  onRecipeClick,
  onLike,
  onComment,
  onAddComment,
  onDeleteComment,
  onSave,
  isLiked = false,
  isSaved = false,
  isOwnPost = false,
  saving = false,
  inlineComments = false,
  onVisible,
  className = ''
}) => {
  const cardRef = useRef(null);
  const hasTriggeredVisible = useRef(false);

  // Visibility tracking
  useEffect(() => {
    if (!onVisible || hasTriggeredVisible.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTriggeredVisible.current) {
            hasTriggeredVisible.current = true;
            onVisible(post);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [onVisible, post]);

  const likeCount = post.like_count || post.likes?.length || 0;
  const commentCount = post.comment_count || post.comments?.length || 0;
  const comments = post.comments || [];

  const [showComments, setShowComments] = useState(false);
  const [showAllComments, setShowAllComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showLikesModal, setShowLikesModal] = useState(false);

  // Lazy load media
  const [media, setMedia] = useState(post.media?.length > 0 ? post.media : null);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [mediaError, setMediaError] = useState(false);

  useEffect(() => {
    const shouldLoad = (post.has_media === true || post.media_count > 0) &&
      (!media || media.length === 0) && !loadingMedia && !mediaError && post.buzz_id;
    if (shouldLoad) {
      setLoadingMedia(true);
      fetch(`${window.location.origin}/api/buzz/${post.buzz_id}/media`)
        .then(res => res.ok ? res.json() : Promise.reject('Failed'))
        .then(data => {
          if (data.media?.length > 0) setMedia(data.media);
          setLoadingMedia(false);
        })
        .catch(() => { setMediaError(true); setLoadingMedia(false); });
    }
  }, [post.has_media, post.media_count, post.buzz_id, media, loadingMedia, mediaError]);

  const handleSubmitComment = async () => {
    if (!commentText.trim() || !onAddComment) return;
    setSubmitting(true);
    try {
      await onAddComment(post.buzz_id, commentText.trim());
      setCommentText('');
      setShowComments(true);
    } catch (err) {
      console.error('Failed to add comment:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const getImageSrc = (m) => {
    if (!m?.data) return null;
    return m.data.startsWith('data:') ? m.data : `data:image/jpeg;base64,${m.data}`;
  };

  // System posts - keep existing compact layout
  if (post.post_type === 'member_joined') {
    return (
      <div
        ref={cardRef}
        className={`bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-200 overflow-hidden ${className}`}
        data-testid={`buzz-card-system-${post.buzz_id}`}
      >
        <div className="p-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
            <UtensilsCrossed size={14} className="text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-800 font-medium truncate">
              {post.system_message || `${post.user_name} joined the hive!`}
            </p>
            <p className="text-[10px] text-gray-500">{formatTimeAgo(post.created_at)}</p>
          </div>
          {post.user_picture ? (
            <img src={post.user_picture} alt="" className="w-7 h-7 rounded-full object-cover border border-amber-200" />
          ) : (
            <div className="w-7 h-7 rounded-full bg-amber-200 flex items-center justify-center text-amber-700 font-bold text-xs">
              {(post.user_name || 'U')[0].toUpperCase()}
            </div>
          )}
        </div>
        {inlineComments && (
          <div className="px-3 pb-3 pt-1.5 border-t border-amber-200 bg-amber-50/50">
            {comments.length > 0 && (
              <div className="mb-2 space-y-1">
                {comments.slice(-3).map((c) => (
                  <div key={c.comment_id} className="flex items-start gap-1.5 text-xs">
                    <span className="font-medium text-gray-700">{c.user_name}:</span>
                    <span className="text-gray-600 flex-1">{c.text}</span>
                    <span className="text-[10px] text-gray-400 flex-shrink-0">{formatTimeAgo(c.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmitComment(); } }}
                placeholder="Welcome them..."
                className="flex-1 px-3 py-1.5 text-xs border border-amber-200 rounded-full bg-white focus:outline-none focus:ring-1 focus:ring-amber-400"
                disabled={submitting}
              />
              <button
                type="button"
                onPointerDown={(e) => { e.preventDefault(); if (commentText.trim()) handleSubmitComment(); }}
                disabled={submitting}
                className={`p-2 rounded-full transition-colors ${
                  commentText.trim() && !submitting ? 'text-white bg-amber-600' : 'text-gray-400 bg-gray-200'
                }`}
                style={{ touchAction: 'manipulation' }}
              >
                {submitting ? <Loader size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  const hasNewActivity = post.is_new_post || (post.new_comment_count > 0);
  const thumbSrc = media?.length > 0 ? getImageSrc(media[0]) : null;

  return (
    <div
      ref={cardRef}
      className={`bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden relative ${className}`}
      data-testid={`buzz-card-${post.buzz_id}`}
    >
      {/* New activity dot */}
      {hasNewActivity && (
        <div className="absolute top-2 right-2 z-10">
          <div className="w-2.5 h-2.5 bg-blue-500 rounded-full animate-pulse" title={post.is_new_post ? 'New post' : `${post.new_comment_count} new`} />
        </div>
      )}

      {/* Hive context row (feed variant) */}
      {variant === 'feed' && post.hives?.length > 0 && (
        <button
          onClick={() => onHiveClick?.(post.hives[0].hive_id)}
          className="w-full flex items-center gap-1.5 px-3 pt-2 pb-0 text-left hover:bg-amber-50/50 transition-colors"
          data-testid={`buzz-card-hive-link-${post.buzz_id}`}
        >
          <span className="text-sm">{post.hives[0].icon}</span>
          <span className="text-xs font-medium text-gray-500 truncate">{post.hives[0].name}</span>
          {post.hives[0].hive_type === 'private' && <span className="text-[10px]">🔒</span>}
        </button>
      )}

      {/* Card body — thumbnail left, meta right */}
      <div className="flex p-3 gap-3">
        {/* Thumbnail */}
        <button
          onClick={() => onRecipeClick?.(post)}
          className="w-[76px] h-[76px] rounded-xl overflow-hidden bg-gray-100 flex-shrink-0 focus:outline-none"
          data-testid={`buzz-card-thumb-${post.buzz_id}`}
        >
          {loadingMedia ? (
            <div className="w-full h-full flex items-center justify-center">
              <Loader className="animate-spin text-amber-300" size={16} />
            </div>
          ) : thumbSrc ? (
            <img
              src={thumbSrc}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <UtensilsCrossed size={24} className="text-gray-400" strokeWidth={1.5} />
            </div>
          )}
        </button>

        {/* Meta content */}
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            {/* Recipe title or caption as primary text */}
            {post.recipe?.title ? (
              <button
                onClick={() => onRecipeClick?.(post)}
                className="font-semibold text-sm text-gray-900 text-left truncate block w-full hover:text-amber-700 transition-colors"
                data-testid={`buzz-card-recipe-link-${post.buzz_id}`}
              >
                {post.recipe.title}
              </button>
            ) : post.caption ? (
              <p className="font-medium text-sm text-gray-900 line-clamp-2">{post.caption}</p>
            ) : (
              <p className="text-sm text-gray-500 italic">Shared a post</p>
            )}

            {/* User + time */}
            <div className="flex items-center gap-1.5 mt-0.5">
              {post.user_picture ? (
                <img src={post.user_picture} alt="" className="w-4 h-4 rounded-full object-cover" />
              ) : (
                <div className="w-4 h-4 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 font-bold text-[8px]">
                  {(post.user_name || 'U')[0].toUpperCase()}
                </div>
              )}
              <span className="text-xs text-gray-500 truncate">{post.user_name || 'Anonymous'}</span>
              <span className="text-gray-300 text-[10px]">&middot;</span>
              <span className="text-xs text-gray-400 flex-shrink-0">{formatTimeAgo(post.created_at)}</span>
            </div>

            {/* Caption as secondary text (only when recipe title is primary) */}
            {post.recipe?.title && post.caption && (
              <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">{post.caption}</p>
            )}
          </div>

          {/* Actions row */}
          <div className="flex items-center gap-3 pt-1">
            {/* Like */}
            <div className="flex items-center gap-0.5">
              <button
                onClick={() => onLike?.(post.buzz_id)}
                className={`p-0.5 transition-colors ${isLiked ? 'text-red-500' : 'text-gray-400 hover:text-red-400'}`}
                data-testid={`buzz-card-like-${post.buzz_id}`}
              >
                <HeartIcon filled={isLiked} size={16} />
              </button>
              {likeCount > 0 && (
                <button
                  onClick={() => setShowLikesModal(true)}
                  className="text-[11px] text-gray-500 hover:text-gray-700 hover:underline"
                >
                  {likeCount}
                </button>
              )}
            </div>

            {/* Comment toggle */}
            <button
              onClick={() => {
                if (inlineComments) setShowComments(!showComments);
                else onComment?.(post);
              }}
              className={`flex items-center gap-0.5 p-0.5 transition-colors ${
                showComments ? 'text-amber-600' : 'text-gray-400 hover:text-amber-400'
              }`}
              data-testid={`buzz-card-comment-${post.buzz_id}`}
            >
              <CommentIcon size={16} />
              {commentCount > 0 && <span className="text-[11px]">{commentCount}</span>}
              {post.new_comment_count > 0 && (
                <span className="text-[9px] bg-blue-500 text-white px-1 py-px rounded-full ml-0.5 leading-none">
                  {post.new_comment_count}
                </span>
              )}
            </button>

            <div className="flex-1" />

            {/* Save / Bookmark */}
            {(post.recipe_id || post.source_recipe_id) && (
              <button
                onClick={() => onSave?.(post)}
                disabled={saving}
                className={`flex items-center gap-0.5 p-0.5 transition-colors ${
                  isOwnPost || isSaved ? 'text-amber-600' : 'text-gray-400 hover:text-amber-400'
                }`}
                data-testid={`buzz-card-save-${post.buzz_id}`}
              >
                <BookmarkIcon filled={isOwnPost || isSaved} size={16} />
                <span className="text-[11px]">{isOwnPost ? 'Yours' : isSaved ? 'Saved' : 'Save'}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Expandable inline comments */}
      {inlineComments && showComments && (
        <div className="border-t border-gray-100 bg-gray-50/80 px-3 py-2.5" data-testid={`buzz-card-comments-section-${post.buzz_id}`}>
          {comments.length > 0 && (
            <div className="space-y-1.5 mb-2.5">
              {comments.length > 5 && !showAllComments && (
                <button
                  onClick={() => setShowAllComments(true)}
                  className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                >
                  View all {comments.length} comments
                </button>
              )}
              {(showAllComments ? comments : comments.slice(-3)).map((c) => (
                <div key={c.comment_id} className="flex items-start gap-1.5 group">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-1 flex-wrap">
                      <span className="font-medium text-xs text-gray-800">{c.user_name}</span>
                      <span className="text-[10px] text-gray-400">&middot; {formatTimeAgo(c.created_at)}</span>
                    </div>
                    <p className="text-xs text-gray-600 leading-snug">{c.text}</p>
                  </div>
                  {onDeleteComment && (
                    <button
                      onClick={() => onDeleteComment(post.buzz_id, c.comment_id)}
                      className="p-0.5 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                    >
                      <X size={10} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmitComment(); } }}
              placeholder="Add a comment..."
              className="flex-1 px-3 py-1.5 text-xs border border-gray-200 rounded-full bg-white focus:outline-none focus:ring-1 focus:ring-amber-400"
              disabled={submitting}
              data-testid={`buzz-card-comment-input-${post.buzz_id}`}
            />
            <button
              type="button"
              onPointerDown={(e) => { e.preventDefault(); if (commentText.trim()) handleSubmitComment(); }}
              disabled={submitting}
              className={`p-2 min-w-[36px] min-h-[36px] flex items-center justify-center rounded-full transition-colors ${
                commentText.trim() && !submitting ? 'text-white bg-amber-600 hover:bg-amber-700' : 'text-gray-400 bg-gray-200'
              }`}
              style={{ touchAction: 'manipulation' }}
              data-testid={`buzz-card-comment-send-${post.buzz_id}`}
            >
              {submitting ? <Loader size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </div>
        </div>
      )}

      {/* Likes modal */}
      {showLikesModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowLikesModal(false)}
        >
          <div className="bg-white rounded-xl max-w-sm w-full max-h-[60vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-gray-900">Liked by</h3>
              <button onClick={() => setShowLikesModal(false)} className="p-1 hover:bg-gray-100 rounded-full">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(60vh-60px)]">
              {post.like_details?.length > 0 ? (
                <div className="space-y-3">
                  {post.like_details.map((user, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold">
                          {(user.name || 'U')[0].toUpperCase()}
                        </div>
                      )}
                      <span className="font-medium text-gray-900">{user.name}</span>
                    </div>
                  ))}
                </div>
              ) : likeCount > 0 ? (
                <p className="text-gray-500 text-center py-4">{likeCount} {likeCount === 1 ? 'person' : 'people'} liked this</p>
              ) : (
                <p className="text-gray-500 text-center py-4">No likes yet</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuzzCard;
